import React, { useMemo } from "react";

import { scoreColor, IndicatorTable, scoreLabel } from "../../shared";

import { 
    currencyFormatter, 
    createDecimalFormatter,
    createIntFormatter,
    textFormatter 
} from "../../../../../utils/formatter/formatter";

export const HistoricalAnalysis = React.memo(({ ctx }) => {

  const { indicatorData = [], tenantHierarchy, selectedType } = ctx || {};

  const { financialData } = selectedType || {};
  const { revenue = 0, profit = 0, unitsSold = 0 } = financialData || {};

  const { labels } = tenantHierarchy || {};

  const firstAvailableLevel = useMemo(() => {
    if (!selectedType?.levels) return null;
    return Object.values(selectedType.levels).find(val => val != null) ?? null;
  }, [selectedType?.levels]);

  const attributeTags = useMemo(() =>
    Object.entries(selectedType?.levels ?? {})
      .map(([key, val]) => [labels?.[key] ?? key, val])
      .filter(([_, val]) => val != null),
    [selectedType?.levels, labels]
  );

  const typeScoreStyle = useMemo(() =>
    scoreColor(selectedType?.score ?? 0),
    [selectedType?.score]
  );

  const financialCards = useMemo(() => [
    { icon: "💲", label: textFormatter("revenue", { textTransform: 'uppercase' }), value: currencyFormatter.format(revenue) },
    { icon: "📈", label: textFormatter("profit", { textTransform: 'uppercase' }), value: currencyFormatter.format(profit) },
    { icon: "🛍️", label: textFormatter("units sold", { textTransform: 'uppercase' }), value: createIntFormatter().format(unitsSold) },
  ], [revenue, profit, unitsSold]);

  if (!selectedType) return (
    <section style={{
      border: "1px solid #e5e5e5", borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden",
      height: "calc(100vh - 280px)", display: "flex",
      alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa"
    }}>
      <div style={{ textAlign: "center", color: "#ccc" }}>
        <div style={{ fontSize: "36px", marginBottom: 8 }}>📊</div>
        <div style={{ fontSize: "13px" }}>Selecione um tipo para ver a análise</div>
      </div>
    </section>
  );

  return (
    <section style={{
      backgroundColor: "#fff", border: "1px solid #e5e5e5",
      borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 280px)", overflow: "hidden"
    }}>
      <div style={{
        padding: "12px 16px", display: "flex", alignItems: "center",
        justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
        flexShrink: 0, backgroundColor: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{
            inlineSize: 26, blockSize: 26, borderRadius: "50%",
            backgroundColor: "#E8773A", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: "700", fontSize: "13px", flexShrink: 0,
          }}>3</span>
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#1a1a1a" }}>
            Análise Detalhada
          </span>
        </div>
      </div>

      <section style={{ overflowY: "auto", flex: 1, backgroundColor: "#fafafa" }}>
        <div style={{ padding: "12px 16px", backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {attributeTags?.map(([label, val]) => (
              <div key={label} style={{
                fontSize: "11px", color: "#666", border: "1px solid #e8e8e8",
                borderRadius: "20px", padding: "3px 10px", backgroundColor: "#f5f5f5", whiteSpace: "nowrap",
              }}>
                {label}: <strong style={{ color: "#1a1a1a" }}>{val}</strong>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>

          <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 10 }}>
              Performance do Tipo de Produto
            </h4>
            <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
              <div style={{ flexShrink: 0 }}>
                <div style={{ fontSize: "40px", fontWeight: "800", color: typeScoreStyle.color, lineHeight: 1 }}>
                  {createDecimalFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(selectedType.score)}
                </div>
                <p style={{ fontSize: "11px", color: "#bbb", marginTop: 2 }}>out of 10.00</p>
              </div>
              <hgroup>
                <h5 style={{ fontSize: "13px", fontWeight: "700", color: typeScoreStyle.color, marginBottom: 5 }}>
                  {scoreLabel(selectedType.score)}
                </h5>
                <p style={{ fontSize: "12px", color: "#555", lineHeight: 1.6 }}>
                  O índice é calculado com base na venda, lucro e participação <strong>histórica</strong> na marca.  
                  Este produto possui um peso maior para a sua nota na <strong>Participação na Venda em Peças</strong>, por ser um produto básico.
                </p>
                <p style={{ fontSize: "11px", color: "#aaa", marginTop: 5 }}>
                  Consulte a matriz de indicadores abaixo para mais detalhes.
                </p>
              </hgroup>
            </div>
          </div>

          <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 12 }}>
              Matriz de Indicadores por Marca
            </h4>
            <IndicatorTable data={indicatorData} />
          </div>

          <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 2 }}>
              Análise Financeira da última coleção
            </h4>
            <div style={{ fontSize: "11px", color: "#aaa", marginBottom: 12 }}>
              Dados financeiros de {selectedType.name} na marca {firstAvailableLevel}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {financialCards.map(card => (
                <div key={card.label} style={{
                  border: "1px solid #f0f0f0", borderRadius: "10px",
                  padding: "12px 14px", backgroundColor: "#fafafa"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: "14px" }}>{card.icon}</span>
                    <span style={{ fontSize: "10px", fontWeight: "600", color: "#aaa", letterSpacing: "0.06em" }}>
                      {card.label}
                    </span>
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#1a1a1a" }}>{card.value}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </section>
  );
});